import { cssVarOf, type ColorToken } from '@integrate/core';

/*
  The first chart in this client, and therefore the one that sets the rules for the rest.

  Everything graphical in `apps/web` before this was the node card's composition bar, which divides by
  its own total — so it has no scale, no zero and no way to express a negative. Both series these
  analytics panes draw are SIGNED: the stranded timeline's net goes below zero whenever received-TSA fee
  income beats gross exposure, and the transition curve's fees go below zero whenever reverse TSAs
  outweigh received ones. A chart built on the composition bar's percentage-of-total technique would
  render every one of those months as a zero-height bar — the best months of the plan, the ones a reader
  is looking for, vanishing under copy saying nothing happened.

  So the four things this establishes, in order of how badly they were needed:

  1. ONE signed scale. `maxUp` and `maxDown` are measured, the plot is split between them in proportion,
     and euros-per-pixel is identical above and below the zero line. Two scales on one plot is the
     dual-axis mistake wearing a different hat.
  2. UNITS ON THE SURFACE. These panes put an annual run-rate in a tile beside a monthly series, and the
     engine divides by twelve between them. The caption says which, every time, because the numbers are
     an order of magnitude apart and both are plausible.
  3. EVERY VALUE READABLE WITHOUT COLOUR OR HOVER. A `<details>` table twin carries the whole series as
     text; the tooltip is an enhancement. That is also what makes the chart assertable — the e2e suite has
     no geometry assertions anywhere and should not gain any, since a pixel-width test tests the CSS.
     Each column carries its values as `data-*`, and the container carries the axis.
  4. COLOUR FROM A TOKEN, never a hex at the call site. `ColorToken` resolves to the same colour on screen
     and in a deck; `nodeDisplay.colors.status` is the OTHER palette, project state, and for buckets only.

  Not here, deliberately: no gridlines (the zero line is the only rule a signed money chart needs, and
  dashed or heavy grids are noise), no number on every column (chaos, and unread), and no axis ticks on
  the value scale — the extremes are direct-labelled and the table has the rest.
*/

export interface MonthSeries {
  key: string;
  label: string;
  /** Above the zero line or below it. A series never crosses. */
  side: 'up' | 'down';
  color: ColorToken;
  /** Non-negative magnitudes, one per month, index-aligned with `months`. */
  values: number[];
}

/** A one-off cash event: a different unit from the run-rate series, so it gets its own band and scale. */
export interface MonthMarkerSeries {
  key: string;
  label: string;
  color: ColorToken;
  values: number[];
}

/**
 * A direct label on the first or last column would hang outside the plot once centred, so those two
 * anchor to their own edge instead. Nudging the whole chart inward to make room would waste width on
 * every chart to accommodate two columns.
 */
function edgeClass(i: number, n: number): string {
  if (i === 0) return ' at-start';
  if (i === n - 1) return ' at-end';
  return '';
}

const PLOT_H = 150;
const MARKER_H = 34;
/** A column narrower than this stops being clickable, so the plot scrolls instead of compressing. */
const MIN_COL_W = 14;

export function MonthChart({
  testId,
  caption,
  unit,
  months,
  series,
  markers,
  scaleStep,
  format,
  ariaLabel,
  note,
  labelOf = (m) => `M${m}`,
}: {
  testId: string;
  caption: string;
  /** Named on the surface: "€ per month", not "€". */
  unit: string;
  months: number[];
  series: MonthSeries[];
  /**
   * One-off cash, drawn in its own band below the axis with its own scale.
   *
   * The original summed a one-off severance payment into the same stacked column as a monthly run-rate
   * and took the maximum of the two as its vertical scale — so on the shipped sample the entire positive
   * axis was one payment, and every month's actual net exposure sat at a quarter of the height. Any port
   * that then added the y-axis such a chart obviously wants would have labelled a €52,938 one-off as
   * €52,938 per month. A different unit gets a different band.
   */
  markers?: MonthMarkerSeries[];
  scaleStep: number;
  format(value: number): string;
  /**
   * How a month index prints — `M6`, or `Mar 27` once the project's Day 1 has a date. Optional so
   * every chart keeps its index labels until a caller passes `monthLabel`; the axis stays the same
   * clock either way.
   */
  labelOf?(m: number): string;
  /** The headline a screen reader should get instead of the marks. */
  ariaLabel: string;
  note?: string;
}): JSX.Element {
  const up = series.filter((s) => s.side === 'up');
  const down = series.filter((s) => s.side === 'down');

  const totalAt = (list: MonthSeries[], i: number): number =>
    list.reduce((a, s) => a + Math.max(0, s.values[i] ?? 0), 0);

  const maxUp = Math.max(0, ...months.map((_, i) => totalAt(up, i)));
  const maxDown = Math.max(0, ...months.map((_, i) => totalAt(down, i)));
  // A single scale for both halves. `|| 1` only guards the all-zero case, where no mark is drawn anyway.
  const perPx = (maxUp + maxDown) / PLOT_H || 1;
  const upH = Math.round(maxUp / perPx);
  const downH = Math.round(maxDown / perPx);

  const maxMarker = Math.max(
    0,
    ...(markers ?? []).flatMap((s) => months.map((_, i) => Math.max(0, s.values[i] ?? 0))),
  );

  // Direct-labelled selectively: the extreme on each side, which is what a reader looks for first.
  const peakUp = months.reduce((best, _, i) => (totalAt(up, i) > totalAt(up, best) ? i : best), 0);
  const peakDown = months.reduce(
    (best, _, i) => (totalAt(down, i) > totalAt(down, best) ? i : best),
    0,
  );

  return (
    <figure
      className="mchart"
      data-testid={testId}
      data-horizon={months.length ? months[months.length - 1] : 0}
      data-max-up={maxUp}
      data-max-down={maxDown}
    >
      <figcaption>
        {caption} <span className="mchart-unit">{unit}</span>
      </figcaption>

      {/* Always present for two or more series, so identity is never colour alone. */}
      <div className="mchart-legend">
        {[...up, ...down, ...(markers ?? [])].map((s) => (
          <span key={s.key} className="mchart-key">
            <i style={{ background: cssVarOf(s.color) }} aria-hidden="true" />
            {s.label}
          </span>
        ))}
      </div>

      <div className="mchart-scroll">
        <div
          className="mchart-plot"
          role="img"
          aria-label={ariaLabel}
          /*
            One hairline rule across the whole plot rather than a 1px segment per column: with a 2px
            surface gap between columns, a per-column rule reads as a dashed line, and a dashed baseline
            says "threshold" when it is just the zero.
          */
          style={{ minWidth: months.length * MIN_COL_W, ['--zero-top' as string]: `${upH}px` }}
        >
          {months.map((m, i) => {
            const u = totalAt(up, i);
            const d = totalAt(down, i);
            const title = [
              labelOf(m),
              ...[...up, ...down].map((s) => `${s.label}: ${format(s.values[i] ?? 0)}`),
              ...(markers ?? [])
                .filter((s) => (s.values[i] ?? 0) > 0)
                .map((s) => `${s.label}: ${format(s.values[i] ?? 0)}`),
            ].join('\n');
            return (
              <div
                className="mchart-col"
                key={m}
                data-month={m}
                data-up={u}
                data-down={d}
                title={title}
              >
                <div className="mchart-half up" style={{ height: upH }}>
                  {i === peakUp && u > 0 && (
                    <b className={`mchart-tag${edgeClass(i, months.length)}`}>{format(u)}</b>
                  )}
                  {up.map((s) => (
                    <span
                      key={s.key}
                      className="mchart-seg"
                      style={{
                        height: Math.max(0, s.values[i] ?? 0) / perPx,
                        background: cssVarOf(s.color),
                      }}
                    />
                  ))}
                </div>
                <div className="mchart-zero" />
                <div className="mchart-half down" style={{ height: downH }}>
                  {down.map((s) => (
                    <span
                      key={s.key}
                      className="mchart-seg"
                      style={{
                        height: Math.max(0, s.values[i] ?? 0) / perPx,
                        background: cssVarOf(s.color),
                      }}
                    />
                  ))}
                  {i === peakDown && d > 0 && (
                    <b className={`mchart-tag${edgeClass(i, months.length)}`}>{format(d)}</b>
                  )}
                </div>
                {markers?.length ? (
                  <div className="mchart-half marker" style={{ height: MARKER_H }}>
                    {markers.map((s) => {
                      const v = Math.max(0, s.values[i] ?? 0);
                      if (!v) return null;
                      return (
                        <span
                          key={s.key}
                          className="mchart-oneoff"
                          data-oneoff={v}
                          style={{
                            height: maxMarker ? Math.max(3, (v / maxMarker) * MARKER_H) : 0,
                            background: cssVarOf(s.color),
                          }}
                        />
                      );
                    })}
                  </div>
                ) : null}
                {/* Inside the container, so the card can never grow a nested scrollbar for its own axis. */}
                <span className="mchart-tick">
                  {m === months[0] || m % scaleStep === 0 ? labelOf(m) : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {note && <p className="muted mchart-note">{note}</p>}

      <details className="mchart-table">
        <summary>Show the numbers</summary>
        <div className="table-scroll">
          <table className="tsa-table">
            <thead>
              <tr>
                <th>Month</th>
                {[...up, ...down, ...(markers ?? [])].map((s) => (
                  <th className="num" key={s.key}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={m}>
                  <td>M{m}</td>
                  {[...up, ...down, ...(markers ?? [])].map((s) => (
                    <td className="num" key={s.key}>
                      {format(s.values[i] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
